import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { Categories } from 'vscode/vscode/vs/platform/action/common/actionCommonCategories';
import { registerAction2, Action2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { Extensions } from 'vscode/vscode/vs/workbench/common/contributions';
import { Memento } from 'vscode/vscode/vs/workbench/common/memento';
import { NotebookSetting } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { HAS_OPENED_NOTEBOOK } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookContextKeys';
import { NotebookEditorInput } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookEditorInput';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';

const _moduleId = "vs/workbench/contrib/notebook/browser/contrib/gettingStarted/notebookGettingStarted";
const hasOpenedNotebookKey = 'hasOpenedNotebook';
const hasShownGettingStartedKey = 'hasShownNotebookGettingStarted';
let NotebookGettingStarted = class NotebookGettingStarted extends Disposable {
    constructor(_editorService, _storageService, _contextKeyService, _commandService, _configurationService) {
        super();
        const hasOpenedNotebook = HAS_OPENED_NOTEBOOK.bindTo(_contextKeyService);
        const memento = ( (new Memento('notebookGettingStarted2', _storageService)));
        const storedValue = memento.getMemento(0 , 0 );
        if (storedValue[hasOpenedNotebookKey]) {
            hasOpenedNotebook.set(true);
        }
        const needToShowGettingStarted = _configurationService.getValue(NotebookSetting.openGettingStarted) && !storedValue[hasShownGettingStartedKey];
        if (!storedValue[hasOpenedNotebookKey] || needToShowGettingStarted) {
            const onDidOpenNotebook = () => {
                hasOpenedNotebook.set(true);
                storedValue[hasOpenedNotebookKey] = true;
                if (needToShowGettingStarted) {
                    _commandService.executeCommand('workbench.action.openWalkthrough', { category: 'notebooks', step: 'notebookProfile' }, true);
                    storedValue[hasShownGettingStartedKey] = true;
                }
                memento.saveMemento();
            };
            if (_editorService.activeEditor?.typeId === NotebookEditorInput.ID) {
                onDidOpenNotebook();
                return;
            }
            const listener = this._register(_editorService.onDidActiveEditorChange(() => {
                if (_editorService.activeEditor?.typeId === NotebookEditorInput.ID) {
                    listener.dispose();
                    onDidOpenNotebook();
                }
            }));
        }
    }
};
NotebookGettingStarted = ( (__decorate([
    ( (__param(0, IEditorService))),
    ( (__param(1, IStorageService))),
    ( (__param(2, IContextKeyService))),
    ( (__param(3, ICommandService))),
    ( (__param(4, IConfigurationService)))
], NotebookGettingStarted)));
( (Registry.as(Extensions.Workbench))).registerWorkbenchContribution(NotebookGettingStarted, 3 );
registerAction2(class NotebookClearNotebookLayoutAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.notebook.layout.gettingStarted',
            title: ( localize2WithPath(_moduleId, 0, "Reset notebook getting started")),
            f1: true,
            precondition: ( (ContextKeyExpr.equals(`config.${NotebookSetting.openGettingStarted}`, true))),
            category: Categories.Developer,
        });
    }
    run(accessor) {
        const storageService = accessor.get(IStorageService);
        const memento = ( (new Memento('notebookGettingStarted', storageService)));
        const storedValue = memento.getMemento(0 , 0 );
        storedValue[hasOpenedNotebookKey] = undefined;
        memento.saveMemento();
    }
});

export { NotebookGettingStarted };
